import React from 'react';
import { useEmbedNavigate } from '../../../hooks/useEmbedNavigate';
import { Icon } from '../../../assets/icons/Icon';
import styles from './StreakCard.module.scss';

interface StreakRange {
  start: string;
  end: string;
  logged_days: number;
}

interface StreakCardProps {
  currentStreak: number;
  dailyCompletion?: Record<string, boolean>;
  streakRanges?: StreakRange[];
  className?: string;
}

export const StreakCard: React.FC<StreakCardProps> = ({ 
  currentStreak, 
  dailyCompletion = {},
  streakRanges = [],
  className = '' 
}) => {
  const navigate = useEmbedNavigate();
  const isEmptyState = currentStreak === 0;

  const isDateInStreakRange = (dateString: string): boolean => {
    if (streakRanges.length === 0) return false;
    
    const date = new Date(dateString);
    return streakRanges.some(range => {
      const start = new Date(range.start);
      const end = new Date(range.end);
      return date >= start && date <= end;
    });
  };

  const getRecentDaysCompletion = () => {
    const today = new Date();
    const days = [];
    
    // Show 5 days: today + last 4 days
    for (let i = 4; i >= 0; i--) {
      const day = new Date(today);
      day.setDate(today.getDate() - i);
      const dateString = day.toISOString().split('T')[0];
      
      // Pill filled only if daily_completion[date] == true AND date falls within streak_ranges
      const isCompleted = dailyCompletion[dateString] === true && isDateInStreakRange(dateString);
      
      days.push({
        date: dateString,
        isCompleted,
      });
    }
    return days;
  };

  const recentDays = getRecentDaysCompletion();

  const getStreakMessage = () => {
    if (isEmptyState) {
      return 'Start a streak!';
    }
    if (currentStreak >= 10) {
      return `Impressive ${currentStreak}-day streak!`;
    }
    if (currentStreak >= 5) {
      return `Great ${currentStreak}-day streak!`;
    }
    return `${currentStreak}-day streak!`;
  };

  return (
    <div className={`${styles.streakCard} ${className}`}>
      <div className={styles.streakCard__header}>
        <h3 className={styles.streakCard__headerTitle}>Progress</h3>
        <button 
          className={styles.streakCard__viewMore}
          onClick={() => navigate('/leaderboard')}
        >
          View more
        </button>
      </div>
      
      <div className={styles.streakCard__content}>
        <Icon 
          iconName={isEmptyState ? "KeepGoingGreyIcon" : "KeepDoingSmallIcon"}
         width="2.75rem"
            height="3.74rem"
        />
        <h2 className={styles.streakCard__title}>
          {getStreakMessage()}
        </h2>
      </div>
      
      <div className={styles.streakCard__weekProgress}>
        {recentDays.map((day) => (
          <Icon
            key={day.date}
            iconName={day.isCompleted ? "CheckedGreenIconCircle" : "NotCheckedGrayIconCircle"}
            width="1.75rem"
            height="1.75rem"
          />
        ))}
      </div>
    </div>
  );
};
